import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../assets/styles/Admin.module.scss';
import { formatDate } from '../../utils/date';

// Type definitions matching the backend schema
interface TenantListResponse {
  id: number;
  name: string;
  plan: string;
  users: number;
  status: string;
  contact: string;
  chatCount: number;
  diagnosisCount: number;
  proposalCount: number;
  lastLogin: string;
  joinedAt: string;
  projectCount: string;
}

interface UserListResponse {
  id: number;
  userName: string;
  companyName: string;
  contact: string;
  chatCount: number;
  diagnosisCount: number;
  proposalCount: number;
  lastLogin: string;
  joinedAt: string;
  plan: string;
  status: string;
}

const AdminClients: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'company' | 'user'>('company');
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const [clients, setClients] = useState<TenantListResponse[]>([]);
  const [userList, setUserList] = useState<UserListResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [clientsRes, usersRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/clients/tenants`, {
            credentials: 'include',
          }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/clients/users`, {
            credentials: 'include',
          }),
        ]);

        if (clientsRes.ok && usersRes.ok) {
          const clientsData = await clientsRes.json();
          const usersData = await usersRes.json();
          setClients(clientsData);
          setUserList(usersData);
        } else {
          console.error('Failed to fetch data');
        }
      } catch (error) {
        console.error('API Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleRowClick = (clientId: number) => {
    navigate(`/admin/clients/${clientId}`);
  };

  // フィルタリング処理
  const isMatchDate = (dateString: string) => {
    if (!dateFilter || !dateString || dateString === '-') return true;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (dateFilter === '7days') return diffDays <= 7;
    if (dateFilter === '30days') return diffDays <= 30;
    return true;
  };

  const filteredClients = clients.filter((client) => {
    const matchSearch =
      !searchQuery ||
      client.name.includes(searchQuery) ||
      (client.contact && client.contact.includes(searchQuery));
    const matchPlan = !planFilter || client.plan === planFilter;
    const matchStatus = !statusFilter || client.status === statusFilter;
    const matchDate = isMatchDate(client.joinedAt) || isMatchDate(client.lastLogin);

    return matchSearch && matchPlan && matchStatus && matchDate;
  });

  const filteredUsers = userList.filter((user) => {
    const matchSearch =
      !searchQuery ||
      user.userName.includes(searchQuery) ||
      user.companyName.includes(searchQuery) ||
      (user.contact && user.contact.includes(searchQuery));
    const matchPlan = !planFilter || user.plan === planFilter;
    const matchStatus = !statusFilter || user.status === statusFilter;
    const matchDate = isMatchDate(user.joinedAt) || isMatchDate(user.lastLogin);

    return matchSearch && matchPlan && matchStatus && matchDate;
  });

  return (
    <div className={styles.contentBox}>
      <div className={styles.pageHeader}>
        <h1>クライアント管理</h1>
        <p>クライアント企業の契約プランと利用ステータス</p>
      </div>

      <div className={styles.filterContainer}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="企業名、ユーザー名、連絡先で検索..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <select
          className={styles.filterSelect}
          value={planFilter}
          onChange={(e) => setPlanFilter(e.target.value)}
        >
          <option value="">プラン（すべて）</option>
          <option value="standard">standard</option>
          <option value="test">test</option>
        </select>

        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">ステータス（すべて）</option>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>

        <select
          className={styles.filterSelect}
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
        >
          <option value="">期間（すべて）</option>
          <option value="7days">過去1週間以内</option>
          <option value="30days">過去1ヶ月以内</option>
        </select>
      </div>

      <div className={styles.tabContainer}>
        <button
          className={`${styles.tab} ${activeTab === 'company' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('company')}
        >
          企業単位
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'user' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('user')}
        >
          ユーザー単位
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.adminTable}>
          <thead>
            <tr>
              <th>ID</th>
              {activeTab === 'user' && <th>ユーザー名</th>}
              <th>企業名</th>
              {activeTab === 'company' && <th>プラン</th>}
              {activeTab === 'company' && <th>ユーザー数</th>}
              {activeTab === 'company' && <th>プロジェクト数</th>}
              {activeTab === 'company' && <th>ステータス</th>}
              <th>連絡先</th>
              <th>チャット</th>
              <th>診断</th>
              <th>提案</th>
              <th>最終ログイン</th>
              <th>登録日</th>
            </tr>
          </thead>
          <tbody>
            {activeTab === 'company'
              ? filteredClients.map((client) => (
                <tr key={`company-${client.id}`} onClick={() => handleRowClick(client.id)}>
                  <td>{client.id}</td>
                  <td>{client.name}</td>
                  <td>{client.plan}</td>
                  <td>{client.users}</td>
                  <td>{client.projectCount}</td>
                  <td>
                    <span className={`${styles.badge} ${styles[client.status]}`}>
                      {client.status.toUpperCase()}
                    </span>
                  </td>
                  <td>{client.contact}</td>
                  <td>{client.chatCount}</td>
                  <td>{client.diagnosisCount}</td>
                  <td>{client.proposalCount}</td>
                  <td>{formatDate(client.lastLogin)}</td>
                  <td>{formatDate(client.joinedAt)}</td>
                </tr>
              ))
              : filteredUsers.map((user) => (
                <tr key={`user-${user.id}`} onClick={() => handleRowClick(user.id)}>
                  <td>{user.id}</td>
                  <td>{user.userName}</td>
                  <td>{user.companyName}</td>
                  <td>{user.contact}</td>
                  <td>{user.chatCount}</td>
                  <td>{user.diagnosisCount}</td>
                  <td>{user.proposalCount}</td>
                  <td>{formatDate(user.lastLogin)}</td>
                  <td>{formatDate(user.joinedAt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminClients;
